import { z } from 'zod';
import { crmStageTypeSchema } from './crm';

const leadIdentitySchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  campaign_id: z.string().uuid().nullable(),
  person_id: z.string().uuid().nullable(),
  assigned_to: z.string().uuid().nullable(),
  stage_id: z.string().uuid().nullable(),
});
const commissioningContextSchema = z.object({
    version: z.literal(1),
    actor_id: z.string().uuid().nullable(),
    stage_type: crmStageTypeSchema.nullable(),
    campaign: z.object({
      id: z.string().uuid(),
      tenant_id: z.string().uuid(),
      name: z.string().nullable(),
      business_driver_id: z.string().uuid().nullable(),
      allowed_stage_ids: z.array(z.string().uuid()).nullable(),
      customer_list_required: z.boolean(),
    }).strict().nullable(),
   }).strict();

/** New source photographs only. Never validate an old context by inventing defaults. */
export const commissioningCrmLeadSourceSnapshotSchema = leadIdentitySchema.extend({
  _occurred_at: z.string().datetime({ offset: true }).optional(),
  _commissioning: commissioningContextSchema,
});
export type CommissioningCrmLeadSourceSnapshot = z.infer<typeof commissioningCrmLeadSourceSnapshotSchema>;

export const commissioningCrmFormSourceSnapshotSchema = z.object({
  submission_id: z.string().uuid(),
  form_id: z.string().uuid(),
  lead_id: z.string().uuid().nullable(),
  lead_snapshot: leadIdentitySchema.nullable(),
  occurred_at: z.string().datetime({ offset: true }),
  _commissioning: commissioningContextSchema,
});
export type CommissioningCrmFormSourceSnapshot = z.infer<typeof commissioningCrmFormSourceSnapshotSchema>;

export const commissioningCrmCustomerSourceSnapshotSchema = z.object({
  customer_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  person_id: z.string().uuid().nullable(),
  lead_snapshot: leadIdentitySchema,
  occurred_at: z.string().datetime({ offset: true }),
  _commissioning: commissioningContextSchema,
});
export type CommissioningCrmCustomerSourceSnapshot = z.infer<typeof commissioningCrmCustomerSourceSnapshotSchema>;

export const commissioningCrmOrganizationSchema = z.object({
  storeId: z.string().uuid(),
  companyId: z.string().uuid(),
}).strict();

export const commissioningCrmAppointmentSourceSnapshotSchema = z.object({
  appointment_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  revision: z.number().int().positive(),
  occurred_at: z.string().datetime({ offset: true }),
  customer_id: z.string().uuid().nullable(),
  person_id: z.string().uuid().nullable(),
  lead_snapshot: leadIdentitySchema.nullable(),
  customer_campaign_id: z.string().uuid().nullable(),
  organization: commissioningCrmOrganizationSchema.nullable(),
  attribution_error: z.enum(['lead_unavailable', 'lead_campaign_conflict', 'customer_unavailable']).nullable(),
  _commissioning: commissioningContextSchema,
});
export type CommissioningCrmAppointmentSourceSnapshot = z.infer<typeof commissioningCrmAppointmentSourceSnapshotSchema>;

export const commissioningCrmCampaignQueueSourceSnapshotSchema = z.object({
  message_id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  lead_snapshot: leadIdentitySchema.nullable(),
  occurred_at: z.string().datetime({ offset: true }),
  _commissioning: commissioningContextSchema,
});
export type CommissioningCrmCampaignQueueSourceSnapshot = z.infer<typeof commissioningCrmCampaignQueueSourceSnapshotSchema>;

export const commissioningCrmOccurrenceReferenceSchema = z.object({
  version: z.literal(1),
  source: z.enum(['lead_creation', 'stage_change', 'form_submission', 'customer_creation', 'appointment_completion', 'campaign_queue_acceptance']),
  sourceId: z.string().uuid(),
  occurredAt: z.string().datetime({ offset: true }),
  actorId: z.string().uuid().nullable(),
  sourceRevision: z.number().int().positive().optional(),
  sourceOrganization: commissioningCrmOrganizationSchema.nullable().optional(),
}).strict().superRefine((value, ctx) => {
  if ((value.source === 'appointment_completion') !== (value.sourceRevision !== undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceRevision'], message: 'La revisione sorgente è obbligatoria solo per il completamento appuntamento' });
  }
  if ((value.source === 'appointment_completion') !== (value.sourceOrganization !== undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceOrganization'], message: 'La provenienza organizzativa appartiene al completamento appuntamento' });
  }
});
export type CommissioningCrmOccurrenceReference = z.infer<typeof commissioningCrmOccurrenceReferenceSchema>;
